import React, { useState } from 'react';
import { X } from 'lucide-react';
import { TrainingCourse, TrainingRequirement } from '../types';

interface Props {
  requirements: TrainingRequirement[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

export const CourseCreateModal: React.FC<Props> = ({
  requirements,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [courseCode, setCourseCode] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<'SMS' | 'SECURITY' | 'FLIGHT_OPS' | 'GROUND_OPS' | 'DANGEROUS_GOODS' | 'HUMAN_FACTORS'>('SMS');
  const [deliveryMethod, setDeliveryMethod] = useState<'CLASSROOM' | 'CBT_E_LEARNING' | 'SIMULATOR' | 'OJT' | 'BLENDED'>('CLASSROOM');
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [passingScore, setPassingScore] = useState(80);
  const [validityDays, setValidityDays] = useState(365);
  const [selectedReqId, setSelectedReqId] = useState<string>(requirements[0]?.id || '');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        course_code: courseCode,
        title,
        category,
        delivery_method: deliveryMethod,
        duration_minutes: Number(durationMinutes),
        passing_score: Number(passingScore),
        validity_days: Number(validityDays),
        description,
        requirement_ids: selectedReqId ? [selectedReqId] : [],
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-xl">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Define Training Course</h3>
            <p>Establish syllabus, delivery method, and certification criteria</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-grid-2">
              <div className="form-field">
                <label>Course Code *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. CRS-SMS-102"
                  value={courseCode}
                  onChange={(e) => setCourseCode(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Category *</label>
                <select
                  value={category}
                  onChange={(e: any) => setCategory(e.target.value)}
                >
                  <option value="SMS">Safety Management (SMS)</option>
                  <option value="FLIGHT_OPS">Flight Operations</option>
                  <option value="GROUND_OPS">Ground Operations</option>
                  <option value="SECURITY">Aviation Security & Cyber (AVSEC)</option>
                  <option value="DANGEROUS_GOODS">Dangerous Goods (DGR)</option>
                  <option value="HUMAN_FACTORS">Human Factors & CRM</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Course Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. EASA Compliant Hazard Identification & Risk Assessment"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Delivery Method</label>
                <select
                  value={deliveryMethod}
                  onChange={(e: any) => setDeliveryMethod(e.target.value)}
                >
                  <option value="CLASSROOM">Classroom Instruction</option>
                  <option value="CBT_E_LEARNING">E-Learning / CBT</option>
                  <option value="SIMULATOR">Flight Simulator (FFS)</option>
                  <option value="OJT">On-the-Job Training (OJT)</option>
                  <option value="BLENDED">Blended Learning</option>
                </select>
              </div>
              <div className="form-field">
                <label>Linked Requirement (The WHY)</label>
                <select
                  value={selectedReqId}
                  onChange={(e) => setSelectedReqId(e.target.value)}
                >
                  <option value="">Select Requirement (Optional)</option>
                  {requirements.map((req) => (
                    <option key={req.id} value={req.id}>
                      {req.requirement_code} - {req.title.slice(0, 30)}...
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Duration (Minutes)</label>
                <input
                  type="number"
                  min="10"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Passing Score (%)</label>
                <input
                  type="number"
                  min="50"
                  max="100"
                  value={passingScore}
                  onChange={(e) => setPassingScore(Number(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Validity (Days)</label>
                <input
                  type="number"
                  min="30"
                  value={validityDays}
                  onChange={(e) => setValidityDays(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Syllabus & Scope Description</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Learning objectives, regulatory references, and key competencies..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
